import React, { FC, useMemo, useRef, useState } from "react";
import { HiXMark } from "react-icons/hi2";
import { useDebounce } from "usehooks-ts";

import { useAppSelector } from "../../../redux/redux";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../../slices/ContactsSlice";
import { isValidEmail } from "../../../utils/email";
import {
  EmailTemplateRecipient,
  mapCustomerToTemplateRecipient,
} from "../../settings/templates/emailTemplates/emailTemplates.utils";
import { initialsOf } from "./composeEmail.utils";

const AVATAR_COLORS = ["#1B3E9F", "#6B4EFF", "#EA6D24", "#0E7C66", "#C2298A"];

const colorFor = (value: string) => {
  let n = 0;
  for (let i = 0; i < value.length; i++) n = (n + value.charCodeAt(i)) % 997;
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
};

type Props = {
  label: string;
  value: EmailTemplateRecipient[];
  onChange: (value: EmailTemplateRecipient[]) => void;
  placeholder?: string;
  /** Right side of the row (e.g. "Cc / Bcc"). */
  extra?: React.ReactNode;
  autoFocus?: boolean;
};

/**
 * To / Cc / Bcc row: people as chips (clients get a "Client" tag), type a name
 * to search the firm's clients, or type an address and press Enter, Tab or ",".
 */
export const RecipientField: FC<Props> = ({
  label,
  value,
  onChange,
  placeholder,
  extra,
  autoFocus,
}) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [focused, setFocused] = useState(false);
  const search = useDebounce(text.trim(), 250);

  const { data: contacts } = useGetSearchCustomersQuery(
    {
      searchQuery: search || "~~~",
      orgId,
      userId: user.id,
      isActive: true,
    },
    { skip: !orgId || search.length < 2 },
  );

  const suggestions = useMemo(
    () =>
      (search.length < 2 ? [] : contacts || [])
        .filter((c) => c.emailAddress)
        .filter((c) => !value.some((v) => v.email === c.emailAddress))
        .slice(0, 6),
    [contacts, search, value],
  );

  const add = (recipient: EmailTemplateRecipient) => {
    if (
      !value.some(
        (v) => v.email.toLowerCase() === recipient.email.toLowerCase(),
      )
    ) {
      onChange([...value, recipient]);
    }
    setText("");
    setInvalid(false);
  };

  const addTyped = () => {
    const typed = text.trim().replace(/[,;]+$/, "");
    if (!typed) return true;
    if (isValidEmail(typed)) {
      add({ email: typed });
      return true;
    }
    setInvalid(true);
    return false;
  };

  return (
    <div className={"relative border-b border-[#EEF0F3] py-[6px]"}>
      <div className={"flex items-start gap-3"}>
        <span
          className={
            "w-[58px] flex-none text-[13px] leading-[34px] text-[#8A90A2]"
          }>
          {label}
        </span>
        <div
          className={
            "flex min-h-[34px] min-w-0 flex-1 flex-wrap items-center gap-[6px]"
          }
          onClick={() => inputRef.current?.focus()}>
          {value
            .filter((r) => r.email)
            .map((r) => {
              const isClient = !!(r.customerId || r.businessContactId);
              const name = r.name || r.customerName || r.email;
              return (
                <span
                  key={r.email}
                  title={r.email}
                  className={
                    "inline-flex h-7 max-w-full items-center gap-[7px] rounded-[14px] border pl-[3px] pr-[6px] text-[13px] font-semibold text-[#1F2433]"
                  }
                  style={{
                    background: isClient ? "#F4F1FF" : "#F3F4F7",
                    borderColor: isClient ? "#E4DCFF" : "#E6E8EE",
                  }}>
                  <span
                    className={
                      "inline-flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full text-[9px] font-bold text-white"
                    }
                    style={{ background: colorFor(r.email) }}>
                    {initialsOf(name)}
                  </span>
                  <span className={"truncate"}>{name}</span>
                  {isClient && (
                    <span
                      className={
                        "whitespace-nowrap rounded-[5px] bg-white px-[6px] py-[1px] text-[11px] font-bold text-[#6B4EFF]"
                      }>
                      Client
                    </span>
                  )}
                  <button
                    type={"button"}
                    aria-label={`Remove ${r.email}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(value.filter((v) => v.email !== r.email));
                    }}
                    className={
                      "inline-flex rounded-full p-[2px] text-[#A3A9B8] hover:bg-[#E6E8EE] hover:text-[#3A4256]"
                    }>
                    <HiXMark size={12} />
                  </button>
                </span>
              );
            })}
          <input
            ref={inputRef}
            autoFocus={autoFocus}
            value={text}
            placeholder={value.length ? "" : placeholder}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              if (text.includes("@")) addTyped();
            }}
            onChange={(e) => {
              const v = e.target.value;
              setInvalid(false);
              if (/[,;]\s*$/.test(v) && v.trim().length > 1) {
                setText(v);
                const typed = v.trim().replace(/[,;]+$/, "");
                if (isValidEmail(typed)) {
                  add({ email: typed });
                  return;
                }
              }
              setText(v);
            }}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === "Tab") && text.trim()) {
                if (suggestions[0] && !text.includes("@")) {
                  e.preventDefault();
                  add(mapCustomerToTemplateRecipient(suggestions[0]));
                  return;
                }
                if (!addTyped() || e.key === "Enter") e.preventDefault();
              } else if (e.key === "Backspace" && !text && value.length) {
                onChange(value.slice(0, -1));
              }
            }}
            className={
              "h-[30px] min-w-[140px] flex-1 border-none bg-transparent px-[2px] text-[14px] font-semibold text-[#1F2433] outline-none placeholder:font-medium placeholder:text-[#A3A9B8]"
            }
          />
        </div>
        {extra}
      </div>
      {invalid && text.trim() && (
        <div
          className={
            "pl-[70px] pt-1 text-[12.5px] font-semibold text-[#E5484D]"
          }>
          {`"${text.trim()}" isn't a valid email address.`}
        </div>
      )}
      {focused && suggestions.length > 0 && (
        <div
          className={
            "absolute left-[70px] top-[calc(100%-2px)] z-40 flex w-[360px] flex-col rounded-[11px] border border-[#E8E8EC] bg-white p-[6px] shadow-[0_14px_36px_rgba(20,22,34,0.18)]"
          }>
          {suggestions.map((c) => (
            <button
              type={"button"}
              key={c.customerId + c.emailAddress}
              onMouseDown={(e) => {
                e.preventDefault();
                add(mapCustomerToTemplateRecipient(c));
              }}
              className={
                "flex items-center gap-[10px] rounded-[8px] px-[10px] py-2 text-left hover:bg-[#F4F2FF]"
              }>
              <span
                className={
                  "inline-flex h-7 w-7 flex-none items-center justify-center rounded-full text-[10px] font-bold text-white"
                }
                style={{ background: colorFor(c.emailAddress) }}>
                {initialsOf(c.customerName)}
              </span>
              <span className={"flex min-w-0 flex-1 flex-col"}>
                <span className={"text-[13.5px] font-bold text-[#1F2433]"}>
                  {c.customerName}
                </span>
                <span className={"truncate text-[12px] text-[#9097A8]"}>
                  {c.emailAddress}
                </span>
              </span>
              <span
                className={
                  "flex-none rounded-[6px] bg-[#F3F4F7] px-[7px] py-[3px] text-[11.5px] font-bold text-[#3A4256]"
                }>
                Client
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
